import React from 'react';
import { Alert } from 'react-bootstrap';
import { Pb3 } from '../../../components/P';
import { Button, Result, Modal } from 'antd';
import { useGetIsEnrollmentBlocked } from '../../../hooks/useGetIsEnrollmentBlocked';
import { useCancelRegistration } from '../../../hooks/useCancelRegistration';
import { useNavigate } from 'react-router-dom';
import { useClear } from '../../../hooks/localSchedule/useClear';

const AbandonedCart = ({ children }: { children: React.ReactNode }) => {
  const navigate = useNavigate();
  const isEnrollmentBlocked = useGetIsEnrollmentBlocked();
  const cancelRegistrationMutation = useCancelRegistration();
  const clearLocalSchedule = useClear();

  // Debug: log the enrollment blocked data and clear local schedule if abandoned cart exists
  React.useEffect(() => {
    if (isEnrollmentBlocked.data) {
      // Clear local schedule when abandoned cart is detected
      clearLocalSchedule();
    }
  }, [
    isEnrollmentBlocked.data,
    isEnrollmentBlocked.isError,
    isEnrollmentBlocked.error,
    clearLocalSchedule,
  ]);

  // If there's an error, still loading, or no pending contracts, don't render anything
  if (
    isEnrollmentBlocked.isLoading ||
    isEnrollmentBlocked.isError ||
    !isEnrollmentBlocked.data
  ) {
    return children;
  }

  const handlePayNow = () => {
    // Navigate to the finalize page with the correct URL structure
    const currentlySchedulingId =
      isEnrollmentBlocked.data?.currentlySchedulingId ||
      isEnrollmentBlocked.data?.id;

    if (currentlySchedulingId) {
      // Always go to payment step (step 2) for pending enrollments
      // Don't include paymentIntent in URL to ensure we go to payment step
      navigate(`/finalize/${currentlySchedulingId}`);
    } else {
      // Fallback to general finalize page
      navigate('/finalize');
    }
  };

  // EnrollmentBlocked.id can be a CurrentlyScheduling ID. Only the explicit
  // StudentRegistrationWindow ID is safe for the cancellation endpoint.
  const registrationId = isEnrollmentBlocked.data.studentRegistrationWindowId;
  const canCancelRegistration =
    Number.isSafeInteger(registrationId) && Number(registrationId) > 0;

  const handleCancelRegistration = () => {
    Modal.confirm({
      title: 'Cancel Registration',
      content:
        'Are you sure you want to cancel this registration? This action cannot be undone.',
      okText: 'Yes, Cancel',
      cancelText: 'No, Keep',
      okType: 'danger',
      onOk: () => {
        if (!canCancelRegistration) return;

        // Use the mutation hook to cancel the registration
        cancelRegistrationMutation.mutate(String(registrationId));
      },
    });
  };

  return (
    <Pb3>
      <Alert style={{ padding: 0 }} variant="warning">
        <Result
          status="warning"
          title={`You have a pending payment`}
          subTitle="Please complete the process or cancel the registration."
          extra={[
            <Button type="primary" onClick={handlePayNow} key="pay">
              Pay Now
            </Button>,
            <Button
              type="default"
              onClick={handleCancelRegistration}
              key="cancel"
              loading={cancelRegistrationMutation.isLoading}
              disabled={
                cancelRegistrationMutation.isLoading || !canCancelRegistration
              }
              title={
                !canCancelRegistration
                  ? 'Reload this registration before canceling. Its registration information is incomplete.'
                  : undefined
              }
            >
              Cancel Registration
            </Button>,
          ]}
        />
      </Alert>
    </Pb3>
  );
};

export default AbandonedCart;
